import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Plus } from 'lucide-react';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import { Modal } from '../components/Modal';
import LoadingState from '../components/LoadingState';
import StatusBadge from '../components/StatusBadge';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import SearchInput from '../components/ui/SearchInput';
import SegmentedControl from '../components/ui/SegmentedControl';
import { EmptyState, ErrorState } from '../components/ui/States';
import { useToast } from '../context/ToastContext';
import { fetchConfirmedOrders } from '../lib/confirmedOrderApi';
import {
  ClientInvoiceDto,
  createManualClientInvoice,
  fetchClientInvoices,
  uploadManualClientInvoice,
} from '../lib/clientInvoiceApi';
import { formatDate as fmtDate } from "../utils/dateFormat";
import { clientDisplayName, orderRef } from "../utils/orderIdentity";
import { formatCurrency } from '../utils/priceUtils';
import { num } from '../utils/invoiceLineMath';

type CreateMode = 'confirmed' | 'manual' | 'upload';

const INFO = (
  <div className="flex flex-col gap-3">
    <p>Client invoices saved here are either created from confirmed orders or entered manually.</p>
    <p className="font-medium">You can:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>Create from a confirmed order</li>
      <li>Create manually with full invoice fields</li>
      <li>Upload a PDF/image invoice and save it as a client invoice record</li>
    </ul>
  </div>
);

// formatCurrency (utils/priceUtils) is the labelled-money owner and never throws:
// a stored code like 'TT$' made Intl.NumberFormat throw RangeError and blank the list.
function money(value: number | null | undefined, currency = '') {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  // D96.14: an invoice with no currency shows a bare number, never a guessed one.
  if (!currency) return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return formatCurrency(value, currency, { ceil: false });
}

// Same two the server accepts (utils/invoiceCurrency.js SUPPORTED_COST_CURRENCIES).
const CURRENCIES = ['TTD', 'USD'] as const;

function CurrencySelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <select
      value={value}
      aria-label="Currency"
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
    >
      {/* D96.14: no default - the person picks. */}
      <option value="">Choose...</option>
      {CURRENCIES.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </select>
  );
}

function formatDate(value?: string) {
  return fmtDate(value, '--');
}

// The status pill and its colour map moved to components/StatusBadge, so this
// screen and every other one classify a status the same way (docs/DESIGN.md).

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'saved', label: 'Saved' },
  { value: 'ready', label: 'Ready' },
  { value: 'sent', label: 'Sent' },
  { value: 'partially_paid', label: 'Partially paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

/** Never the raw error: the list either loaded or it did not, and the user's
 *  next move is the same either way. */
const LOAD_FAILED = 'Client invoices could not be loaded. Check your connection and try again.';

const CREATE_MODES = [
  { value: 'manual', label: 'Manual' },
  { value: 'upload', label: 'Upload' },
  { value: 'confirmed', label: 'From confirmed order' },
] as const;

/**
 * The manual form's two typed numbers. `num` (the one client parser) reads
 * "1,200" as 1200 and junk as null; the old `Number(x || 0)` turned "1,200"
 * into NaN (stored null) and a blank into a typed 0. Blank stays missing (null),
 * junk is refused with a message, and a count must be a whole number >= 0.
 */
export function parseManualAmounts(form: { totalPrice: string; totalItems: string }):
  | { ok: true; quotedTotal: number | null; packingCount: number | null }
  | { ok: false; error: string } {
  const blank = (v: string) => String(v ?? '').trim() === '';
  const quotedTotal = blank(form.totalPrice) ? null : num(form.totalPrice);
  if (!blank(form.totalPrice) && quotedTotal === null) {
    return { ok: false, error: 'Total price must be a number, like 1200 or 1,200.50.' };
  }
  const packingCount = blank(form.totalItems) ? null : num(form.totalItems);
  if (!blank(form.totalItems) && (packingCount === null || !Number.isInteger(packingCount) || packingCount < 0)) {
    return { ok: false, error: 'Total items must be a whole number, 0 or more.' };
  }
  return { ok: true, quotedTotal, packingCount };
}

/** Human text for a refused create, from the server's machine code (never its prose). */
export function createErrorText(e: any, fallback: string): string {
  switch (e?.code) {
    case 'CLIENT_INVOICE_NUMBER_TAKEN':
      return 'That invoice number is already used by another invoice. Pick a different number or leave it blank.';
    case 'CLIENT_INVOICE_CURRENCY_REQUIRED':
      return 'Pick the invoice currency (TTD / USD) first.';
    case 'CLIENT_INVOICE_SUMMARY_INVALID':
      return 'The total price could not be read. Enter a number, zero or more.';
    case 'CLIENT_INVOICE_CURRENCY_INVALID':
      return 'That currency is not supported. Choose TTD or USD.';
    default:
      return fallback;
  }
}

function manualSummary(a: { quotedTotal: number | null; packingCount: number | null }) {
  return {
    quotedTotal: a.quotedTotal,
    actualCostTotal: 0,
    markupValueTotal: 0,
    profitVariance: a.quotedTotal,
    profitMarginPct: null,
    discrepancyCount: 0,
    returnCount: 0,
    packingCount: a.packingCount,
    matchedPoCount: 0,
  };
}

export function formatConfirmedOrderLabel(order: any) {
  const ref = orderRef(order) || order?.emailSubject || order?.notes || '';
  const client = clientDisplayName(order?.clientName || order?.emailFrom);
  const vessel = order?.vesselName ? `Vessel: ${order.vesselName}` : '';
  const meta = [
    order?.status ? `Status: ${String(order.status).toUpperCase()}` : '',
    order?.confirmedAt ? `Date: ${fmtDate(order.confirmedAt)}` : '',
  ].filter(Boolean);
  const fallbackId = order?._id ? `${String(order._id).slice(0, 8)}…` : 'Confirmed Order';
  const parts = [ref, client, vessel, ...meta].filter(Boolean);
  return parts.length > 0 ? parts.join(' | ') : fallbackId;
}

export const ClientInvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<ClientInvoiceDto[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [mode, setMode] = useState<CreateMode>('manual');
  const [confirmedOrders, setConfirmedOrders] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [selectedConfirmedOrderId, setSelectedConfirmedOrderId] = useState('');
  const [manualForm, setManualForm] = useState({
    invoiceNumber: '',
    clientName: '',
    clientEmail: '',
    vesselName: '',
    invoiceDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    currency: '',
    totalPrice: '',
    totalItems: '',
    notes: '',
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchClientInvoices(page, limit, search, status);
      setItems(res.clientInvoices || []);
      setTotal(res.pagination?.total || 0);
      setError(null);
    } catch {
      // A failed request used to render the same "No client invoices found." an
      // empty result does, so an outage read as an empty ledger. The two are
      // now told apart: an error gets a retry, an empty list gets a next step.
      setItems([]);
      setTotal(0);
      setError(LOAD_FAILED);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (page !== 1) {
        setPage(1);
      } else {
        load();
      }
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openAdd = async () => {
    setAddOpen(true);
    try {
      const res = await fetchConfirmedOrders(1, 100, '');
      setConfirmedOrders(res.confirmedOrders || []);
    } catch {
      setConfirmedOrders([]);
    }
  };

  const amounts = parseManualAmounts(manualForm);
  const amountError = amounts.ok ? null : amounts.error;

  const createManual = async () => {
    if (!amounts.ok) return;
    setSaving(true);
    try {
      const payload: any = {
        source: 'manual',
        invoiceNumber: manualForm.invoiceNumber || undefined,
        clientName: manualForm.clientName,
        clientEmail: manualForm.clientEmail,
        vesselName: manualForm.vesselName,
        invoiceDate: manualForm.invoiceDate,
        dueDate: manualForm.dueDate || undefined,
        currency: manualForm.currency,
        notes: manualForm.notes,
        summary: manualSummary(amounts),
        items: [],
      };
      const created = await createManualClientInvoice(payload);
      setAddOpen(false);
      navigate(`/client-invoices/${created._id}`);
    } catch (e: any) {
      toast.error(createErrorText(e, 'Failed to create client invoice'));
    } finally {
      setSaving(false);
    }
  };

  const uploadInvoice = async () => {
    if (!uploadFile) {
      toast.error('Choose a file first');
      return;
    }
    if (!amounts.ok) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('source', 'upload');
      fd.append('invoiceNumber', manualForm.invoiceNumber || '');
      fd.append('clientName', manualForm.clientName || '');
      fd.append('clientEmail', manualForm.clientEmail || '');
      fd.append('vesselName', manualForm.vesselName || '');
      fd.append('invoiceDate', manualForm.invoiceDate || '');
      fd.append('dueDate', manualForm.dueDate || '');
      fd.append('currency', manualForm.currency);
      fd.append('notes', manualForm.notes || '');
      fd.append('summary', JSON.stringify(manualSummary(amounts)));
      const created = await uploadManualClientInvoice(fd);
      setAddOpen(false);
      navigate(`/client-invoices/${created._id}`);
    } catch (e: any) {
      toast.error(createErrorText(e, 'Failed to upload client invoice'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            {/* The sidebar calls this screen "Client Invoices"; the page now agrees. */}
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Client Invoices</h1>
            <ModuleInfoButton content={INFO} ariaLabel="Client invoice information" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput value={search} onChange={setSearch} placeholder="Search here" className="w-48" />
            <Button variant="primary" onClick={openAdd} icon={<Plus aria-hidden="true" className="h-4 w-4" />}>
              Add
            </Button>
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Drafts, saved invoices, and sent invoices are all managed here. Create from a confirmed order or enter one manually.
        </p>
      </div>

      <div className="px-4 py-4 flex-1 overflow-auto sm:px-8">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200">
            <SegmentedControl
              aria-label="Status"
              value={status}
              // A new filter starts at page 1: page 3 of "all" is usually past the
              // end of "paid", which read as "No client invoices found".
              onChange={(next) => {
                setStatus(next);
                setPage(1);
              }}
              segments={STATUS_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
            />
          </div>

          {error ? (
            <div className="p-4">
              <ErrorState message={error} onRetry={load} />
            </div>
          ) : (
            <div className="overflow-auto">
              {/* `data-table` (index.css) supplies the header, cell padding and row
                  hover, so only genuine per-cell overrides stay. */}
              <table className="data-table min-w-[1100px]">
                <thead>
                  <tr>
                    <th>Invoice number</th>
                    <th>Client name</th>
                    <th>Created</th>
                    {/* Item 5 (9 Oct QA retest): the list had no due-date or payment-terms
                        columns, though both fields already exist and are populated on the
                        document (ClientInvoice.dueDate / .paymentTerms). */}
                    <th>Due date</th>
                    <th>Payment terms</th>
                    <th className="text-right">Total price</th>
                    <th className="text-right">Total items</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={9} className="py-8">
                        <LoadingState text="Loading client invoices..." />
                      </td>
                    </tr>
                  )}
                  {!loading && items.length === 0 && (
                    <tr>
                      <td colSpan={9} className="py-10">
                        <EmptyState
                          title="No client invoices found"
                          detail="Use Add to create a manual invoice or open one from a confirmed order."
                        />
                      </td>
                    </tr>
                  )}
                  {items.map((item) => (
                    <tr key={item._id} className="cursor-pointer" onClick={() => navigate(`/client-invoices/${item._id}`)}>
                      <td className="font-medium text-slate-900">{item.invoiceNumber}</td>
                      <td>{clientDisplayName(item.clientName) || '--'}</td>
                      <td>{formatDate(item.createdAt || item.invoiceDate)}</td>
                      <td>{formatDate(item.dueDate)}</td>
                      <td>{item.paymentTerms || '--'}</td>
                      <td className="text-right tabular-nums">
                        {/* D56 — summary.quotedTotal is now explicitly the
                            PRE-discount subtotal, not the invoice's total
                            (clientInvoice.service.buildClientInvoiceSummary).
                            "Total price" means what the client actually owes,
                            so this reads the Grand Total; quotedTotal is kept
                            only as the fallback for an invoice saved before
                            invoiceGrandTotal existed (AccountsPage.tsx uses
                            the same fallback order for the same reason). */}
                        {money(
                          Number(item.summary?.invoiceGrandTotal ?? item.summary?.quotedTotal ?? 0),
                          item.currency || ''
                        )}
                      </td>
                      <td className="text-right tabular-nums">{Number(item.summary?.packingCount || item.items?.length || 0)}</td>
                      <td>
                        <StatusBadge status={item.status} />
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/client-invoices/${item._id}`);
                            }}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            aria-label={`Edit invoice ${item.invoiceNumber}`}
                            icon={<Pencil aria-hidden="true" className="h-4 w-4" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/client-invoices/${item._id}`);
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination page={page} pageSize={limit} total={total} onPageChange={setPage} noun="invoices" />
        </div>
      </div>

      <Modal isOpen={addOpen} title="Add client invoice" onClose={() => setAddOpen(false)} maxWidthClass="max-w-4xl">
        <div className="space-y-4">
          <div className="border-b border-slate-200 pb-3">
            <SegmentedControl
              aria-label="How to create the invoice"
              value={mode}
              onChange={setMode}
              segments={CREATE_MODES.map((m) => ({ value: m.value, label: m.label }))}
            />
          </div>

          {mode === 'confirmed' && (
            <div className="space-y-3">
              <label className="block text-xs font-medium text-slate-600">Confirmed order</label>
              <select
                value={selectedConfirmedOrderId}
                onChange={(e) => setSelectedConfirmedOrderId(e.target.value)}
                className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm"
              >
                <option value="">Select confirmed order</option>
                {confirmedOrders.map((co) => (
                  <option key={co._id} value={co._id}>
                    {formatConfirmedOrderLabel(co)}
                  </option>
                ))}
              </select>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                This will open the invoice flow for the selected confirmed order.
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!selectedConfirmedOrderId) return;
                    setAddOpen(false);
                    navigate(`/confirmed-orders/${selectedConfirmedOrderId}/invoice`);
                  }}
                  disabled={!selectedConfirmedOrderId}
                >
                  Open flow
                </Button>
              </div>
            </div>
          )}

          {mode === 'manual' && (
            <div className="space-y-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                This creates a draft client invoice header. You can open it afterwards and add line items.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ['invoiceNumber', 'Invoice number'],
                ['clientName', 'Client name'],
                ['clientEmail', 'Client email'],
                ['vesselName', 'Vessel name'],
                ['invoiceDate', 'Invoice date', 'date'],
                ['dueDate', 'Due date', 'date'],
                ['currency', 'Currency'],
                ['totalPrice', 'Total price'],
                ['totalItems', 'Total items'],
              ].map((field: any) => (
                <div key={field[0]} className={field[0] === 'notes' ? 'md:col-span-2' : ''}>
                  <label className="block text-xs font-medium text-slate-600 mb-1">{field[1]}</label>
                  {field[0] === 'currency' ? (
                    <CurrencySelect
                      value={manualForm.currency}
                      onChange={(v) => setManualForm((prev) => ({ ...prev, currency: v }))}
                    />
                  ) : (
                    <input
                      type={field[2] || 'text'}
                      value={(manualForm as any)[field[0]]}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, [field[0]]: e.target.value }))}
                      className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm"
                    />
                  )}
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                <textarea
                  value={manualForm.notes}
                  onChange={(e) => setManualForm((prev) => ({ ...prev, notes: e.target.value }))}
                  className="w-full h-24 rounded-md border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              {amountError && (
                <p role="alert" className="md:col-span-2 text-xs text-red-600">
                  {amountError}
                </p>
              )}
              <div className="md:col-span-2 flex items-center justify-end gap-2 pt-2">
                <Button onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={createManual}
                  loading={saving}
                  disabled={!manualForm.clientName.trim() || !amounts.ok || !manualForm.currency}
                >
                  Create
                </Button>
                </div>
              </div>
            </div>
          )}

          {mode === 'upload' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Invoice file</label>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="w-full text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Client name</label>
                  <input
                    value={manualForm.clientName}
                    onChange={(e) => setManualForm((prev) => ({ ...prev, clientName: e.target.value }))}
                    className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Invoice number</label>
                  <input
                    value={manualForm.invoiceNumber}
                    onChange={(e) => setManualForm((prev) => ({ ...prev, invoiceNumber: e.target.value }))}
                    className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Currency</label>
                  <CurrencySelect
                    value={manualForm.currency}
                    onChange={(v) => setManualForm((prev) => ({ ...prev, currency: v }))}
                  />
                </div>
              </div>
              {amountError && (
                <p role="alert" className="text-xs text-red-600">
                  {amountError}
                </p>
              )}
              <div className="flex items-center justify-end gap-2">
                <Button onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button variant="primary" onClick={uploadInvoice} loading={saving} disabled={!uploadFile || !amounts.ok || !manualForm.currency}>
                  Upload
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
